/**
 * Client Instance ID utility
 *
 * This generates and maintains a stable client instance ID that persists across
 * page refreshes, route changes, and token refreshes. This ID is used to identify
 * the device/browser instance for session conflict detection.
 *
 * The ID is stored in localStorage and only changes when:
 * - User explicitly clears localStorage
 * - User reinstalls the app
 * - ID is explicitly reset
 */

const CLIENT_INSTANCE_ID_KEY = "coachmate_client_instance_id";

/**
 * Gets or generates a stable client instance ID
 * The ID is generated once and stored in localStorage
 * @returns The client instance ID (UUID format)
 */
export const getClientInstanceId = (): string => {
  try {
    let clientInstanceId = localStorage.getItem(CLIENT_INSTANCE_ID_KEY);

    if (!clientInstanceId) {
      clientInstanceId = crypto.randomUUID();
      localStorage.setItem(CLIENT_INSTANCE_ID_KEY, clientInstanceId);
    }

    return clientInstanceId;
  } catch (error) {
    console.warn("localStorage not available, using temporary instance ID");
    return crypto.randomUUID();
  }
};

/**
 * Resets the client instance ID (generates a new one)
 * This should only be called when explicitly needed (e.g., user wants to reset device)
 * @returns The new client instance ID
 */
export const resetClientInstanceId = (): string => {
  try {
    const newId = crypto.randomUUID();
    localStorage.setItem(CLIENT_INSTANCE_ID_KEY, newId);
    return newId;
  } catch (error) {
    console.error("Failed to reset client instance ID:", error);
    return crypto.randomUUID();
  }
};

/**
 * Checks if a client instance ID exists
 * @returns true if an ID exists in localStorage
 */
export const hasClientInstanceId = (): boolean => {
  try {
    return !!localStorage.getItem(CLIENT_INSTANCE_ID_KEY);
  } catch (error) {
    return false;
  }
};
